import ListSubheader from '@material-ui/core/ListSubheader';
import { AutocompleteRenderGroupParams } from '@mui/material/Autocomplete';
import React, { HTMLAttributes } from 'react';
import { ListChildComponentProps, VariableSizeList } from 'react-window';
import { makeStyles } from 'tss-react/mui';

import { typography12MediumStyle } from 'src/legacy/components/Text';
import { GraySmall } from 'src/theme/colors';

const useStyles = makeStyles()(() => ({
  listbox: {
    '& .MuiListSubheader-root ': {
      color: GraySmall,
      ...typography12MediumStyle,
    },
    // reduce the height of the options
    '& .MuiAutocomplete-option': {
      minHeight: 'auto',
    },
  },
}));

const LISTBOX_PADDING = 6; // px
const DEFAULT_ITEM_HEIGHT = 34;
const LIST_ITEM = 8;
const OVERSCANCOUNT = 5;

function renderRow(props: ListChildComponentProps) {
  const { data, index, style } = props;
  return React.cloneElement(data[index], {
    style: {
      ...style,
      top: (style.top as number) + LISTBOX_PADDING,
    },
  });
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef<HTMLDivElement>((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

function useResetCache(data: any) {
  const ref = React.useRef<VariableSizeList>(null);
  React.useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [data]);
  return ref;
}

interface GroupListboxComponentProps extends HTMLAttributes<HTMLDivElement> {
  itemHeight?: number;
}

/**
 * @description This is deprecated. Please use the new GroupListboxComponent component in src/components/select/GroupListBox.tsx
 * @deprecated use the new GroupListboxComponent in src/components/select/GroupListBox.tsx
 */
export const GroupListboxComponent = React.forwardRef<
  HTMLDivElement,
  GroupListboxComponentProps
>((props, ref) => {
  const { children, itemHeight = DEFAULT_ITEM_HEIGHT, ...other } = props;
  const itemData = React.Children.toArray(children);
  const itemCount = itemData.length;

  const { classes } = useStyles();

  const getChildSize = (child: React.ReactNode) => {
    if (React.isValidElement(child) && child.type === ListSubheader) {
      return itemHeight;
    }

    return itemHeight;
  };

  const getHeight = () => {
    if (itemCount > LIST_ITEM) {
      return LIST_ITEM * itemHeight;
    }
    return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
  };

  const gridRef = useResetCache(itemCount);
  return (
    <div ref={ref} className={classes.listbox}>
      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          itemData={itemData}
          height={getHeight()}
          width="100%"
          ref={gridRef}
          outerElementType={OuterElementType}
          innerElementType="ul"
          itemSize={(index) => getChildSize(itemData[index])}
          overscanCount={OVERSCANCOUNT}
          itemCount={itemCount}
        >
          {renderRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});
type ListboxComponentProps = HTMLAttributes<HTMLDivElement>;
export const ListboxComponent = (props: ListboxComponentProps) => {
  return <GroupListboxComponent {...props} />;
};

/**
 * @description This is deprecated. Please use the new RenderGroup component in src/components/select/GroupListBox.tsx
 * @deprecated use the new RenderGroup in src/components/select/GroupListBox.tsx
 */

export const RenderGroup = (params: AutocompleteRenderGroupParams) => [
  params.group ? (
    <ListSubheader
      key={params.key}
      component="div"
      className="flex items-center"
    >
      {params.group}
    </ListSubheader>
  ) : null,

  params.children,
];
